import React, { FC } from 'react';
import { Page, Text, View, Document, StyleSheet, Image, Font, pdf } from '@react-pdf/renderer';
import { Style } from '@react-pdf/types';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import fileDownload from 'js-file-download';
import QRCode from 'qrcode';

// Lib
import api from '@/lib/api';
import { helpers } from '@/lib/helpers';

// Services
import gsService from '@/services/gsService';

// Types
import { DCFile } from '@/@types/lib/dataController';
import { standardHeadersWithAuth } from '@/lib/api/standardHeadersWithAuth';

// Icons
import NorthEast from './Icons/NorthEastIcon';
import SouthEast from './Icons/SouthEastIcon';
import AccessTime from './Icons/AccessTime';
import SettingsEthernet from './Icons/SettingsEthernet';

// Custom
import Watermark from './Watermark';
import LogoIconForPDF from '@/icons/LogoIconForPDF';

// Register font
Font.register({
  family: 'Roboto',
  fonts: [
    {
      src: 'https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-light-webfont.ttf',
      fontWeight: 300,
    },
    {
      src: 'https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-regular-webfont.ttf',
      fontWeight: 400,
    },
    {
      src: 'https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-medium-webfont.ttf',
      fontWeight: 500,
    },
    {
      src: 'https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-bold-webfont.ttf',
      fontWeight: 600,
    },
  ],
});

Font.registerHyphenationCallback((word) => [word]);

// Create styles
const styles = StyleSheet.create({
  text: {
    color: '#000',
    fontFamily: 'Roboto',
  },
  statsContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignContent: 'flex-end',
    width: '90mm',
  },
  statsBox: {
    width: '17mm',
    display: 'flex',
    flexDirection: 'column',
    alignContent: 'center',
  },
  statsValue: {
    fontSize: 14,
  },
  statsLabel: {
    fontSize: 10,
  },
  page: {
    padding: '10mm',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
  },
  section: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  squareBox: {
    width: '90mm',
    height: '90mm',
  },
});

const mapLayers = [
  'HGSS:OSM',
  'HGSS:zp_np',
  'HGSS:staze_ferrate',
  'HGSS:msp',
  'HGSS:staze_plan',
  'HGSS:zp_pp',
  'HGSS:zp_sr',
  'HGSS:staze_ostale',
  'HGSS:drzavna_granica',
  'HGSS:staze_ldt',
  'HGSS:staze_ldt_via_adriatica',
  'HGSS:staze_ldt_cldt',
  'HGSS:staze_ldt_via_dinarica',
  'HGSS:izlet_dan',
  'HGSS:izlet_dan_route_points',
];

export const generatePublicIzletDocument = (recordId: string) => {
  const apiInstance = new api();

  apiInstance.Call(`public/izleti/${recordId}/pdf`, 'get').then((resp) => {
    if (resp.success) {
      const data = resp.data as IzletDataType;
      data.plan_izleta = data.plan_izleta?.map((izlet) => ({
        ...izlet,
        gsUrl: gsService.getImageUrl(mapLayers, izlet.id, izlet.bbox, 400, 400),
      }));

      const config = {
        position: undefined,
        extent: undefined,
        maplayers: undefined,
        route_points: undefined,
        obilaznica_id: undefined,
      };

      const shareData = {
        config: JSON.stringify(config),
        object_uuid: recordId,
        object_type: 'izlet',
      };

      apiInstance.Call('share-generate', 'post', shareData).then((resp_share) => {
        // @ts-ignore
        if (resp_share.success && resp_share?.data?.url_hash) {
          // @ts-ignore
          const shareUrl = `${window.location.origin}/web-share?hash=${resp_share.data.url_hash}`;
          QRCode.toDataURL(shareUrl, { width: 300, margin: 2, color: { dark: '#005a96' } }, async (err, url) => {
            if (err) {
              console.error(err);
              return;
            }

            const document = <IzletDocument data={{ ...data, qrUrl: url } as IzletDataType} />;

            if (document) {
              pdf(document)
                .toBlob()
                .then((file) => {
                  fileDownload(file, `izlet_${new Date().valueOf()}.pdf`);
                });
            }
          });
        }
      });
    }
  });
};

export const generateIzletDocument = (recordId: number, onFinishCallback: () => void, showPreview?: boolean) => {
  const apiInstance = new api();

  try {
    apiInstance.Call(`hps/izleti/${recordId}/pdf`, 'get').then((resp) => {
      if (resp.success) {
        const data = resp.data as IzletDataType;
        data.plan_izleta = data.plan_izleta?.map((izlet) => ({
          ...izlet,
          gsUrl: gsService.getImageUrl(mapLayers, izlet.id, izlet.bbox, 400, 400),
        }));

        const config = {
          position: undefined,
          extent: undefined,
          maplayers: undefined,
          route_points: undefined,
          obilaznica_id: undefined,
        };

        const shareData = {
          config: JSON.stringify(config),
          object_uuid: data.uuid,
          object_type: 'izlet',
        };

        apiInstance.Call('share-generate', 'post', shareData).then((resp_share) => {
          // @ts-ignore
          if (resp_share.success && resp_share?.data?.url_hash) {
            // @ts-ignore
            const shareUrl = `${window.location.origin}/web-share?hash=${resp_share.data.url_hash}`;
            QRCode.toDataURL(shareUrl, { width: 300, margin: 2, color: { dark: '#005a96' } }, async (err, url) => {
              if (err) {
                console.error(err);
                return;
              }

              const document = <IzletDocument data={{ ...data, qrUrl: url } as IzletDataType} isPrivate showPreview={showPreview} />;

              if (document) {
                pdf(document)
                  .toBlob()
                  .then((file) => {
                    fileDownload(file, `izlet_${new Date().valueOf()}.pdf`);
                    onFinishCallback();
                  });
              }
            });
          }
        });
      }
    });
  } catch (e) {
    console.warn('Error during PDF generation');
    onFinishCallback();
  }
};

type IzletDataType = {
  active: boolean;
  created_by: number;
  created_on: string;
  drzava_ttoken: string;
  id: number;
  uuid?: string;
  image: DCFile;
  interna_napomena: string;
  is_otkazan: boolean;
  modified_by?: number;
  modified_on?: string;
  odrediste: string;
  opisi: Array<{
    poglavlje_id: number;
    naziv_ttoken: string;
    sadrzaj: string;
  }>;
  organizator: string;
  organizator_logotip_url: string;
  organizator_logotip_base64: string;
  organizator_logotip_contenttype: string;
  plan_izleta: Array<{
    id: number;
    dan: number;
    datum: string;
    dan_u_tjednu: string;
    opis: string;
    bbox: [number, number, number, number];
    gsUrl?: string;
    total_length?: number;
    total_time_min?: number;
    total_ascent?: number;
    total_descent?: number;
  }>;
  prvi_dan: string;
  dan_u_tjednu: string;
  trajanje_dani: number;
  vodici: Array<string>;
  qrUrl: string;
};

type IzletDocumentProps = {
  data: IzletDataType;
  isPrivate?: boolean;
  showPreview?: boolean;
};

const { getDayTokenForNumOfDays, formatMinutes } = helpers;

// Create Document Component
const IzletDocument: FC<IzletDocumentProps> = (props) => {
  const { data, isPrivate, showPreview } = props;

  const { t } = useTranslation();

  return (
    <Document language="hr">
      <Page size="A4" style={styles.page}>
        {/* naslov */}
        <View style={[styles.section, { height: '30mm', width: '190mm', marginBottom: '5mm' }]}>
          <View style={{ width: '140mm' }}>
            <View style={{ width: '140mm', flexDirection: 'row', alignItems: 'center', gap: '2mm', marginBottom: '5mm' }}>
              <LogoIconForPDF />
              <Text style={[styles.text, { fontSize: 22, color: '#005a96' }]}>{t('hps.izleti.sigurni_izlet')}</Text>
            </View>
            {/* ime izleta i logoi */}
            <Text style={[styles.text, { fontSize: 18, color: '#005a96', flexWrap: 'wrap' }]}>{`${data.odrediste}`}</Text>
            <Text style={[styles.text, { fontSize: 12 }]}>{t(data?.drzava_ttoken as string)}</Text>
          </View>
          <View style={{ width: '70mm', display: 'flex', flexDirection: 'row', justifyContent: 'flex-end', gap: '5mm' }}>
            {data.organizator_logotip_base64 ? (
              <Image
                style={{ maxHeight: '30mm' }}
                src={`data:${data.organizator_logotip_contenttype};base64,${data.organizator_logotip_base64}`}
              />
            ) : null}
            <Image style={{ maxHeight: '30mm' }} src="/images/hps_logo.png" />
          </View>
        </View>
        {/* info o izletu */}
        <View style={[styles.section, { width: '190mm', marginBottom: '3mm', gap: '5mm' }]}>
          <CardSection label={t('hps.izleti.datum_polaska')} value={`${moment(data.prvi_dan).format('dddd, D.M.YYYY.')}`} />
          <CardSection
            label={t('hps.izleti.trajanje_izleta')}
            value={`${data.trajanje_dani} ${t(getDayTokenForNumOfDays(data.trajanje_dani))}`}
          />
          <CardSection label={t('hps.izleti.organizator')} value={data?.organizator} />
          <CardSection label={t('hps.izleti.vodici')} value={data?.vodici ? data.vodici.join('\n') : undefined} />
        </View>
        <View style={[styles.section, { width: '190mm', marginBottom: '3mm', gap: '5mm' }]}>
          <CardSection label={t('hps.izlet_poglavlja.zahtjevnost')} value={data.opisi?.find((x) => x.poglavlje_id === 5)?.sadrzaj || ''} />
        </View>
        <View style={{ width: '190mm', height: 1, backgroundColor: '#005a96', marginBottom: '3mm' }} />
        {/* opceniti opis i slika izleta */}
        <View style={[styles.section, { width: '190mm', marginBottom: '3mm', gap: '10mm' }]}>
          <View style={{ flexBasis: '90mm', flexGrow: 1 }}>
            {data?.opisi ? (
              <Text style={[styles.text, { fontSize: 10 }]}>{data.opisi?.find((x) => x.poglavlje_id === 1)?.sadrzaj}</Text>
            ) : null}
          </View>
          {data.image.content ? (
            <Image src={`data:image/jpg;base64,${data.image.content}`} style={{ width: '90mm', height: '90mm', objectFit: 'cover' }} />
          ) : null}
        </View>
        {/* plan izleta */}
        <View style={[styles.section, { width: '190mm', marginBottom: '1mm' }]}>
          <Text style={[styles.text, { fontSize: 22, color: '#005a96' }]}>{t('hps.izlet_poglavlja.plan_izleta')}</Text>
        </View>
        <View style={[styles.section, { flexDirection: 'column', width: '190mm', marginBottom: '3mm' }]}>
          {data?.plan_izleta
            ? data.plan_izleta
                .sort((x, y) => x.dan - y.dan)
                .map((izlet, index) => (
                  <View style={{ marginBottom: '5mm' }}>
                    {index > 0 ? <View style={{ width: '190mm', height: 1, backgroundColor: '#005a96', marginBottom: '3mm' }} /> : null}
                    <Text style={[styles.text, { color: '#005a96', fontSize: 18 }]}>
                      {`${izlet.dan}. ${t('common.day.day').toLowerCase()} / ${moment(izlet.datum).format('dddd, D.M.YYYY.')}`}
                    </Text>
                    <View style={[styles.section, { width: '140mm', marginBottom: '5mm', gap: '10mm' }]}>
                      <Text style={[styles.text, { fontSize: 10 }]}>{izlet.opis}</Text>
                    </View>
                    {izlet.bbox[0] ? (
                      <View style={[styles.section, { width: '190mm', marginBottom: '5mm' }]} minPresenceAhead={500}>
                        <View style={{ width: '1px', height: '90mm' }} />
                        <View style={[styles.squareBox, { marginRight: '10mm' }]}>
                          <Image src={izlet.gsUrl} />
                        </View>
                        <View style={[styles.squareBox]}>
                          <Image
                            src={{
                              uri: `/api/v1/${isPrivate ? 'hps' : 'public'}/izleti/${data.id}/dani/${izlet.id}/profile`,
                              method: 'GET',
                              headers: standardHeadersWithAuth(),
                              body: null,
                            }}
                            // style={{ width: '100%' }}
                          />
                          {izlet.total_length && izlet.total_time_min && izlet.total_ascent && izlet.total_descent ? (
                            <View style={[styles.statsContainer, {}]}>
                              <View style={[styles.statsBox]}>
                                <SettingsEthernet fill="#005a96" />
                                <Text style={[styles.text, styles.statsValue, { color: '#005a96' }]}>{`${
                                  izlet.total_length as number
                                }`}</Text>
                                <Text style={[styles.text, styles.statsLabel]}>{`${t('route.duljina') as string} (m)`}</Text>
                              </View>

                              <View style={[styles.statsBox]}>
                                <AccessTime fill="#005a96" />
                                <Text style={[styles.text, styles.statsValue, { color: '#005a96' }]}>{`${formatMinutes(
                                  izlet.total_time_min as number
                                )}`}</Text>
                                <Text style={[styles.text, styles.statsLabel]}>{t('route.trajanje') as string}</Text>
                              </View>

                              <View style={[styles.statsBox]}>
                                <NorthEast fill="#005a96" />
                                <Text style={[styles.text, styles.statsValue, { color: '#005a96' }]}>{`${
                                  izlet.total_ascent as number
                                } m`}</Text>
                                <Text style={[styles.text, styles.statsLabel]}>{t('route.elevation_gain') as string}</Text>
                              </View>

                              <View style={[styles.statsBox]}>
                                <SouthEast fill="#005a96" />
                                <Text style={[styles.text, styles.statsValue, { color: '#005a96' }]}>{`${
                                  izlet.total_descent as number
                                } m`}</Text>
                                <Text style={[styles.text, styles.statsLabel]}>{t('route.elevation_loss') as string}</Text>
                              </View>
                            </View>
                          ) : null}
                        </View>
                      </View>
                    ) : null}
                  </View>
                ))
            : null}
        </View>
        {/* ostale informacije */}
        <View style={[styles.section, { width: '190mm', marginBottom: '5mm' }]}>
          <Text style={[styles.text, { fontSize: 22, color: '#005a96' }]}>{t('hps.izlet_poglavlja.dodatne_informacije')}</Text>
        </View>
        <View style={[styles.section, { flexWrap: 'wrap', width: '190mm', marginBottom: '5mm', gap: '5mm' }]}>
          <CardSection
            style={{ flexBasis: '90mm' }}
            label={t('hps.izlet_poglavlja.mjesto_vrijeme_polaska')}
            value={data.opisi?.find((x) => x.poglavlje_id === 3)?.sadrzaj}
          />
          <CardSection
            style={{ flexBasis: '90mm' }}
            label={t('hps.izlet_poglavlja.povratak')}
            value={data.opisi?.find((x) => x.poglavlje_id === 4)?.sadrzaj}
          />
          <CardSection
            style={{ flexBasis: '90mm' }}
            label={t('hps.izlet_poglavlja.prijevoz')}
            value={data.opisi?.find((x) => x.poglavlje_id === 8)?.sadrzaj}
          />
          <CardSection
            style={{ flexBasis: '90mm' }}
            label={t('hps.izlet_poglavlja.prehrana')}
            value={data.opisi?.find((x) => x.poglavlje_id === 7)?.sadrzaj}
          />
          <CardSection
            style={{ flexBasis: '90mm' }}
            label={t('hps.izlet_poglavlja.oprema')}
            value={data.opisi?.find((x) => x.poglavlje_id === 6)?.sadrzaj}
          />
          <CardSection
            style={{ flexBasis: '90mm' }}
            label={t('hps.izlet_poglavlja.dokumenti')}
            value={data.opisi?.find((x) => x.poglavlje_id === 11)?.sadrzaj}
          />
          <CardSection
            style={{ flexBasis: '90mm' }}
            label={t('hps.izlet_poglavlja.troskovi')}
            value={data.opisi?.find((x) => x.poglavlje_id === 10)?.sadrzaj}
          />
          <CardSection
            style={{ flexBasis: '90mm' }}
            label={t('hps.izlet_poglavlja.odustanak')}
            value={data.opisi?.find((x) => x.poglavlje_id === 12)?.sadrzaj}
          />
        </View>
        <View style={{ width: '190mm', height: 1, backgroundColor: '#005a96', marginBottom: '5mm' }} />
        <View style={[styles.section, { flexWrap: 'wrap', width: '190mm', marginBottom: '5mm', gap: '5mm' }]}>
          <CardSection
            style={{ flexBasis: '90mm' }}
            label={t('hps.izlet_poglavlja.prijava_non_req')}
            value={data.opisi?.find((x) => x.poglavlje_id === 9)?.sadrzaj}
          />
        </View>
        <View style={{ width: '190mm', height: 1, backgroundColor: '#005a96', marginBottom: '5mm' }} />
        <View style={[styles.section, {}]}>
          <Text style={[styles.text, { fontSize: 10, width: '140mm' }]}>
            {data.opisi?.find((x) => x.poglavlje_id === 13)?.sadrzaj || ''}
          </Text>
          {data.qrUrl ? (
            <Image
              style={{
                width: '40mm',
                height: '40mm',
              }}
              src={data.qrUrl}
            />
          ) : null}
        </View>
        <Text
          fixed
          style={{
            position: 'absolute',
            bottom: 20,
            left: 0,
            right: 0,
            marginTop: '10mm',
            textAlign: 'center',
            fontSize: 10,
            color: 'grey',
          }}
          render={({ pageNumber }) => pageNumber}
        />
        {showPreview ? <Watermark /> : null}
      </Page>
    </Document>
  );
};

const CardSection = ({ label, value, style }: { label: string; value: string | undefined; style?: Style | Style[] }) =>
  value ? (
    <View style={{ flexGrow: 1, flexBasis: '40mm', ...style }}>
      <Text style={[styles.text, { fontSize: 10, color: '#005a96' }]}>{label}</Text>
      <Text style={[styles.text, { fontSize: 10 }]}>{value}</Text>
    </View>
  ) : null;

export default IzletDocument;
